import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IoSend, IoClose } from 'react-icons/io5';
import { Client } from '@stomp/stompjs';
import {
  getOrCreateBnbHostThread,
  getOrCreateTenantLandlordThread,
  getThreadMessages,
  sendMessage,
  markThreadAsRead,
} from '../../../api/services/chatService';
import { getProfile, getAuthToken } from '../../../api/services/authService';
import API_BASE_URL from '../../../api/config/apiConfig';
import './GuestChatModal.css';

const GuestChatModal = ({
  isOpen,
  onClose,
  propertyId,
  guestUserId,
  guestName = 'Guest',
  propertySubtitle = '',
  chatMode = 'bnb',
  onThreadOpened,
}) => {
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);

  const messagesEndRef = useRef(null);
  const initKeyRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, []);

  const loadMessages = useCallback(async (threadId) => {
    try {
      const res = await getThreadMessages(threadId, 0, 30);
      const rawMessages = Array.isArray(res) ? res : (res?.content || res?.data || []);
      const normalized = [...rawMessages].reverse().map((msg) => ({
        ...msg,
        id: msg.id || msg._id || null,
        text: msg.text || msg.body || '',
        time: new Date(msg.timestamp || msg.createdAt || Date.now()).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        }),
      }));
      setMessages(normalized);
      await markThreadAsRead(threadId).catch(() => {});
      setTimeout(() => scrollToBottom(), 50);
    } catch (err) {
      console.error('GuestChatModal loadMessages error:', err);
      setMessages([]);
    }
  }, [scrollToBottom]);

  useEffect(() => {
    if (!isOpen || !propertyId) return;

    const initKey = `${propertyId}:${guestUserId || 'current-user'}:${chatMode}`;
    if (initKeyRef.current === initKey) return;
    initKeyRef.current = initKey;

    let cancelled = false;

    const initChat = async () => {
      setLoading(true);
      setError('');
      setThread(null);
      setMessages([]);

      try {
        const profileRes = await getProfile().catch(() => null);
        const profile = profileRes?.data || profileRes || {};
        const myId = profile.id || profile.userId || null;
        if (!cancelled) setCurrentUserId(myId);

        if (guestUserId && myId && String(myId) === String(guestUserId)) {
          throw new Error('Cannot message yourself. This booking may be missing the guest user ID.');
        }

        const options = {
          guestName,
          currentUserId: myId,
        };

        if (guestUserId) {
          options.tenantUserId = guestUserId;
          options.guestUserId = guestUserId;
        } else if (myId && chatMode === 'bnb') {
          options.guestName =
            guestName ||
            profile.fullName ||
            profile.name ||
            [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim() ||
            'Guest';
        }

        const openedThread =
          chatMode === 'landlord'
            ? await getOrCreateTenantLandlordThread(propertyId, options)
            : await getOrCreateBnbHostThread(propertyId, options);

        if (cancelled) return;

        const threadId = openedThread?.id || openedThread?._id || openedThread?.threadId;
        if (!threadId) {
          throw new Error('Failed to open conversation: missing thread identifier.');
        }

        setThread(openedThread);
        window.dispatchEvent(new CustomEvent('app:thread_opened', { detail: openedThread }));
        await loadMessages(threadId);
        if (cancelled) return;
        if (typeof onThreadOpened === 'function') {
          onThreadOpened(openedThread);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Failed to open conversation.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    initChat();

    return () => {
      cancelled = true;
    };
  }, [isOpen, propertyId, guestUserId, guestName, chatMode, loadMessages, onThreadOpened]);

  useEffect(() => {
    if (!isOpen) {
      initKeyRef.current = null;
      setThread(null);
      setMessages([]);
      setMessageInput('');
      setError('');
    }
  }, [isOpen]);

  useLayoutEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (!isOpen || !thread?.id) return;

    const token = getAuthToken();
    const client = new Client({
      brokerURL: `${API_BASE_URL.replace('http', 'ws')}/ws-native?token=${token}`,
      connectHeaders: { Authorization: `Bearer ${token}` },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    client.onConnect = () => {
      client.subscribe(`/topic/chat/threads/${thread.id}`, (message) => {
        if (!message.body) return;
        try {
          const eventData = JSON.parse(message.body);
          if (eventData.event !== 'MESSAGE_CREATED') return;

          const incomingMsg = eventData.message || eventData.payload || eventData.data;
          if (incomingMsg?.id) {
            setMessages((prev) => {
              if (prev.some((m) => String(m.id) === String(incomingMsg.id))) return prev;
              const normalizedMsg = {
                ...incomingMsg,
                id: incomingMsg.id || incomingMsg._id || null,
                text: incomingMsg.text || incomingMsg.body || incomingMsg.content || '',
                time: new Date(
                  incomingMsg.createdAt || incomingMsg.timestamp || Date.now()
                ).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              };
              return [...prev, normalizedMsg];
            });
            setTimeout(() => scrollToBottom(), 50);
          } else {
            setTimeout(() => {
              loadMessages(thread.id).catch(() => {});
            }, 400);
          }
        } catch {
          /* ignore parse errors */
        }
      });
    };

    client.activate();

    return () => {
      if (client.active) client.deactivate();
    };
  }, [isOpen, thread?.id, loadMessages, scrollToBottom]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const text = messageInput.trim();
    if (!text || !thread?.id || sending) return;

    const tempId = Date.now();
    const optimistic = {
      id: tempId,
      text,
      isMine: true,
      isOptimistic: true,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setMessages((prev) => [...prev, optimistic]);
    setMessageInput('');
    setSending(true);

    try {
      const recipientUserId = thread.recipientUserId || thread.intendedRecipientUserId || thread.chatWithUserId || guestUserId;
      const realMessage = await sendMessage(thread.id, {
        text,
        recipientUserId,
        receiverUserId: recipientUserId,
        targetUserId: recipientUserId,
        chatWithUserId: recipientUserId,
        tenantUserId: recipientUserId,
        guestUserId: recipientUserId,
        propertyId,
        chatMode,
      });
      setMessages((prev) => {
        const withoutTemp = prev.filter((m) => String(m.id) !== String(tempId));
        if (withoutTemp.some((m) => String(m.id) === String(realMessage.id))) return withoutTemp;
        return [
          ...withoutTemp,
          {
            ...realMessage,
            text: realMessage.text || realMessage.body || text,
            isMine: true,
            time: new Date(
              realMessage.timestamp || realMessage.createdAt || Date.now()
            ).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          },
        ];
      });
    } catch (err) {
      setMessages((prev) => prev.filter((m) => String(m.id) !== String(tempId)));
      setError(err?.message || 'Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  const displayName = thread?.displayName || guestName;
  const propertyLabel = thread?.displaySubtitle || propertySubtitle || '';
  const headerTitle = propertyLabel ? `${displayName} - ${propertyLabel}` : displayName;

  return createPortal(
    <div className="guest-chat-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="guest-chat-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Chat with ${headerTitle}`}
      >
        <header className="guest-chat-modal__header">
          <div className="guest-chat-modal__avatar">{displayName.charAt(0).toUpperCase()}</div>
          <div className="guest-chat-modal__titles">
            <h3>{headerTitle}</h3>
            {thread?.chatWithKind ? (
              <p className="guest-chat-modal__kind">{thread.chatWithKind}</p>
            ) : null}
          </div>
          <button type="button" className="guest-chat-modal__close" onClick={onClose} aria-label="Close chat">
            <IoClose size={22} />
          </button>
        </header>

        <div className="guest-chat-modal__body">
          {loading ? (
            <div className="guest-chat-modal__state">Opening conversation…</div>
          ) : error ? (
            <div className="guest-chat-modal__state guest-chat-modal__state--error">{error}</div>
          ) : messages.length === 0 ? (
            <div className="guest-chat-modal__state">No messages yet. Start the conversation!</div>
          ) : (
            messages.map((msg) => {
              const isMine =
                msg.isMine ||
                msg.sender === 'me' ||
                (currentUserId && String(msg.senderId) === String(currentUserId));
              return (
                <div
                  key={msg.id}
                  className={`guest-chat-modal__bubble ${isMine ? 'guest-chat-modal__bubble--me' : 'guest-chat-modal__bubble--other'}`}
                >
                  <div className="guest-chat-modal__bubble-text">{msg.text}</div>
                  <div className="guest-chat-modal__bubble-time">{msg.time}</div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {!loading && !error && thread?.id && (
          <form className="guest-chat-modal__input-row" onSubmit={handleSendMessage}>
            <input
              type="text"
              placeholder="Type your message..."
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              disabled={sending}
            />
            <button type="submit" disabled={!messageInput.trim() || sending} aria-label="Send message">
              <IoSend size={18} />
            </button>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
};

export default GuestChatModal;
